import apiClient, { ApiResponse } from '../../../shared/utils/axios';
import type {
  GenerateReportRequest,
  GenerateReportResponse,
  SaveReportRequest,
  SaveReportResponse,
  ListSavedReportsResponse,
  GetSavedReportResponse,
  UpdateSavedReportRequest,
  UpdateSavedReportResponse,
  DeleteSavedReportResponse,
  GenerateFromSavedRequest,
  GenerateReportResponse as GenerateFromSavedResponse,
  DashboardData,
  ExportDashboardRequest,
  DetailedReportRequest,
} from './types';

class ReportAPI {
  /**
   * Generate a report with specified dimensions, metrics, and filters
   */
  async generateReport(
    params: GenerateReportRequest
  ): Promise<ApiResponse<GenerateReportResponse>> {
    const response = await apiClient.post<GenerateReportResponse>(
      '/dooh_api/analytics/v1/report/generate',
      params
    );
    return response;
  }

  /**
   * Save a report configuration for later use
   */
  async saveReport(
    params: SaveReportRequest
  ): Promise<ApiResponse<SaveReportResponse>> {
    const response = await apiClient.post<SaveReportResponse>(
      '/dooh_api/analytics/v1/report/save',
      params
    );
    return response;
  }

  /**
   * List all saved reports for the current user
   */
  async listSavedReports(
    partnerId?: string
  ): Promise<ApiResponse<ListSavedReportsResponse>> {
    const url = `/dooh_api/analytics/v1/report/saved?partnerId=${partnerId}`;
    const response = await apiClient.get<ListSavedReportsResponse>(url);
    return response;
  }

  /**
   * Get a specific saved report by ID
   */
  async getSavedReport(
    id: string
  ): Promise<ApiResponse<GetSavedReportResponse>> {
    const response = await apiClient.get<GetSavedReportResponse>(
      `/dooh_api/analytics/v1/report/saved/${id}`
    );
    return response;
  }

  /**
   * Update an existing saved report
   */
  async updateSavedReport(
    id: string,
    params: UpdateSavedReportRequest
  ): Promise<ApiResponse<UpdateSavedReportResponse>> {
    const response = await apiClient.put<UpdateSavedReportResponse>(
      `/dooh_api/analytics/v1/report/saved/${id}`,
      params
    );
    return response;
  }

  /**
   * Delete a saved report
   */
  async deleteSavedReport(
    id: string
  ): Promise<ApiResponse<DeleteSavedReportResponse>> {
    const response = await apiClient.delete<DeleteSavedReportResponse>(
      `/dooh_api/analytics/v1/report/saved/${id}`
    );
    return response;
  }

  /**
   * Generate a report from a saved report configuration
   * Optionally override date range and filters
   */
  async generateFromSaved(
    id: string,
    params?: GenerateFromSavedRequest
  ): Promise<ApiResponse<GenerateFromSavedResponse>> {
    const response = await apiClient.post<GenerateFromSavedResponse>(
      `/dooh_api/analytics/v1/report/saved/${id}/generate`,
      params || {}
    );
    return response;
  }

  async getDashboard(partnerId: string): Promise<ApiResponse<DashboardData>> {
    const response = await apiClient.get<DashboardData>(
      `/dooh_api/dashboard?partnerId=${partnerId}`
    );
    return response;
  }

  async exportDashboard(
    params: ExportDashboardRequest
  ): Promise<ApiResponse<string>> {
    const response = await apiClient.post<string>(
      '/dooh_api/dashboard/export',
      params
    );
    return response;
  }

  async downloadDetailedReport(
    params: DetailedReportRequest
  ): Promise<ApiResponse<any>> {
    const response = await apiClient.post<any>(
      '/dooh_api/analytics/v1/report/detailed',
      params,
      { timeout: 300000 }
    );
    return response;
  }
}

export const reportAPI = new ReportAPI();
export default reportAPI;
